import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useDoc } from '../hooks/useDocs';
import DocContent from '../components/DocContent';

const DocPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: doc, isLoading, error } = useDoc(slug);

  // Scroll to section when hash is in URL
  useEffect(() => {
    const scrollToHash = () => {
      if (doc && window.location.hash) {
        const hash = window.location.hash.substring(1); // Remove the # symbol
        const element = document.getElementById(hash);
        if (element) {
          // Use requestAnimationFrame for smoother scrolling
          requestAnimationFrame(() => {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
          });
        }
      }
    };

    // Scroll when doc loads or slug changes
    if (doc) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        scrollToHash();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [doc, slug]);

  // Handle hash changes for in-page navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      const element = document.getElementById(hash);
      if (element) {
        requestAnimationFrame(() => {
          const offset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        });
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  if (isLoading) {
    return <p className="text-slate-500">Loading documentation...</p>;
  }

  if (error || !doc) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-700 dark:border-rose-900/30 dark:bg-rose-900/20">
        Unable to load documentation. {error?.message}
      </div>
    );
  }

  return <DocContent doc={doc} />;
};

export default DocPage;

